import { create } from "zustand";
import axios from "axios";
import { logger } from "@/utils/logger";
import { isMobile, isIOS } from "@/utils/deviceDetection";
import { IStore } from "@/types/interfaces";
import { EMAIL_SIGNUP_REQUEST } from "@/constants/requests";

interface ConfirmDialogState {
  isOpen: boolean;
  message: string;
  resolver?: (confirmed: boolean) => void;
}

interface UIState {
  // Core UI state
  isLoading: boolean;
  loadingProgress: number;

  // Asset loading state
  isAssetLoading: boolean;

  // UI specific states
  isFullscreen: boolean;
  isMobile: boolean;

  // Additional properties for ColorEngine
  isIOS: boolean;
  isRaceChanged: boolean;
  isPoseChanged: boolean;
  isPaintOpen: boolean;
  colorMode: boolean;

  // Menu state for mobile
  isMenuUnfolded: boolean;
  selectedBarItem: string;

  // Confirmation dialog state
  confirmDialog: ConfirmDialogState;
}

interface UIActions extends IStore {
  // Toggle fullscreen
  toggleFullscreen: () => void;

  // Set loading state
  setLoading: (loading: boolean) => void;

  // Set loading progress (0-100)
  setLoadingProgress: (progress: number) => void;

  // Set asset loading state
  setAssetLoading: (loading: boolean) => void;

  setColorMode: (mode: boolean) => void;

  // Set cart item count

  // Toggle race changed state
  setRaceChanged: (value: boolean) => void;

  // Toggle pose changed state
  setPoseChanged: (value: boolean) => void;

  // Toggle paint mode
  togglePaintMode: () => void;

  // Toggle menu fold/unfold state
  toggleMenu: () => void;

  // Update device detection from centralized service
  updateDeviceDetection: () => void;

  // Force refresh device detection (useful for resize events)
  refreshDeviceDetection: () => void;

  // Show a confirmation dialog
  showConfirmDialog: (message: string) => Promise<boolean>;

  // Resolve the current confirmation dialog
  resolveConfirmDialog: (confirmed: boolean) => void;

  // Send email signup to backend
  sendEmailSignup: (email: string) => Promise<void>;

  setSelectedBarItem: (key: string) => void;
}

const initialState: UIState = {
  isLoading: true,
  loadingProgress: 0,
  isAssetLoading: false,
  isFullscreen: false,
  isMobile: false,
  isIOS: false,
  isRaceChanged: false,
  isPoseChanged: false,
  isPaintOpen: false,
  colorMode: false,
  isMenuUnfolded: true,
  selectedBarItem: "",
  confirmDialog: {
    isOpen: false,
    message: "",
    resolver: undefined
  }
};

export const useUIStore = create<UIState & UIActions>((set: any, get: any) => ({
  ...initialState,

  initialize: async (): Promise<boolean> => {
    try {
      set((state: UIState) => ({
        ...state,
        isLoading: true,
        loadingProgress: 0
      }));

      // Use centralized device detection
      get().updateDeviceDetection();

      return true;
    } catch (error) {
      logger.error("Failed to initialize UI store", error, "UIStore");
      throw error;
    }
  },

  reset: () => {
    set(initialState);
  },

  getStatus: () => {
    const state = get();
    return {
      isInitialized: !state.isLoading
    };
  },

  toggleFullscreen: () => {
    set((state: UIState) => ({
      ...state,
      isFullscreen: !state.isFullscreen
    }));
  },

  setLoading: (loading: boolean) => {
    set((state: UIState) => ({
      ...state,
      isLoading: loading
    }));
  },

  setLoadingProgress: (progress: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(progress)));
    set((state: UIState) => ({
      ...state,
      loadingProgress: clamped
    }));
  },

  setAssetLoading: (loading: boolean) => {
    set((state: UIState) => ({
      ...state,
      isAssetLoading: loading
    }));
  },

  setRaceChanged: (value: boolean) => {
    set((state: UIState) => ({
      ...state,
      isRaceChanged: !state.isRaceChanged
    }));
  },

  setPoseChanged: (value: boolean) => {
    set((state: UIState) => ({
      ...state,
      isPoseChanged: !state.isPoseChanged
    }));
  },

  setColorMode: (mode: boolean) => {
    set((state: UIState) => ({
      ...state,
      colorMode: mode
    }));
  },

  setPaintOpen: (value: boolean) => {
    set((state: UIState) => ({
      ...state,
      isPaintOpen: !state.isPaintOpen
    }));
  },

  togglePaintMode: () => {
    set((state: UIState) => ({
      ...state,
      isPaintOpen: !state.isPaintOpen
    }));
  },

  toggleMenu: () => {
    set((state: UIState) => ({
      ...state,
      isMenuUnfolded: !state.isMenuUnfolded
    }));
  },

  updateDeviceDetection: () => {
    set((state: UIState) => ({
      ...state,
      isMobile: isMobile(),
      isIOS: isIOS()
    }));
  },

  refreshDeviceDetection: () => {
    get().updateDeviceDetection();
  },

  showConfirmDialog: async (message: string) => {
    return await new Promise<boolean>((resolve) => {
      set((state: UIState) => ({
        ...state,
        confirmDialog: {
          isOpen: true,
          message,
          resolver: (confirmed: boolean) => resolve(confirmed)
        }
      }));
    });
  },

  resolveConfirmDialog: (confirmed: boolean) => {
    const { confirmDialog } = get();
    confirmDialog.resolver?.(confirmed);
    set((state: UIState) => ({
      ...state,
      confirmDialog: {
        isOpen: false,
        message: "",
        resolver: undefined
      }
    }));
  },

  sendEmailSignup: async (email: string): Promise<void> => {
    try {
      const url = `${import.meta.env.VITE_BACKEND}${EMAIL_SIGNUP_REQUEST}`;
      const response = await axios.post(url, { email });

      if (!response || !response.data) {
        throw new Error("Failed to send email signup");
      }

      logger.info("Email signup sent successfully", { email }, "UIStore");
    } catch (error) {
      logger.error("Failed to send email signup", error, "UIStore");
      throw error;
    }
  },

  setSelectedBarItem: (key: string) => {
    set((state: UIState) => ({
      ...state,
      selectedBarItem: key
    }));
  }
}));
